//
// # Browser-side observability shim
//
// Sends structured events to AlphaServer's `POST /api/browser-event`
// endpoint, where they are re-emitted via `logInfo` and land in Cloud
// Logging under `logName=alpha-server` with `metadata.logKind = "browser-event"`.
//
// Use this for any IDE-side observation we want queryable in our normal
// log/alerting stack — sign-in attempts (the original motivator), runtime
// errors that don't reach an API call, "user did X" telemetry, etc.
//
// We must NOT log directly to Cloud Logging from the browser (no GCP
// credentials safely shippable to JS, the SDK is Node-only, and a direct
// pipe would be a DoS / cost surface). Hence this server hop.
//
// Implementation notes:
//   - Uses `fetch` with `keepalive: true` and `credentials: "omit"` so the
//     call is fire-and-forget, doesn't block the user, and survives page
//     unload (so we still capture an event even if the user closes the
//     tab right after triggering it). `navigator.sendBeacon` was the
//     obvious choice, but it unconditionally sends credentials which
//     trips CORS preflight against the server's wildcard
//     `Access-Control-Allow-Origin: *` in dev — see `logFromBrowser`.
//   - Server validates the event tag against a typia literal-union
//     schema. New events require a matching server-side change in
//     `AlphaServer/src/browser_events/routes.ts` (extend the
//     `BrowserEventName` union there).
//   - Best-effort only: a dropped request never throws or breaks the
//     calling flow. Treat browser-side logging as observability for
//     honest browsers, not authoritative auditing.
//

// AlphaServer base URL, injected at startup by `initBrowserLog`. Resolved
// the same way `AlphaClient` resolves its URL — main.tsx hands the value
// down. We avoid reading `COMPILER_DEFS` directly here so the URL-source
// stays consistent with the rest of the IDE's HTTP plumbing.
let alphaServerUrl: string | null = null;

const BROWSER_EVENT_PATH = "/api/browser-event";

/**
 * Initialize the browser-log endpoint URL. Must be called once at IDE
 * startup, before any `logFromBrowser` calls. `main.tsx` already resolves
 * `alphaServerUrl` for `AlphaClient`; pass the same value here.
 */
export function initBrowserLog(url: string): void {
  alphaServerUrl = url;
}

/**
 * Allowed browser event tags. Must stay in sync with the server's
 * `BrowserEventName` literal union in `browser_events/routes.ts`. Typed
 * as a union so call sites get autocomplete and a compile-time check
 * that they picked a registered event.
 */
export type BrowserEvent = "auth-link-requested" | "auth-link-failed";

/**
 * Send a structured event to the server-side log pipe.
 *
 * Fire-and-forget by design — never throws, never awaited, never blocks
 * the caller. If the request fails (network down, server 4xx) the event
 * is lost silently; that is acceptable for observability.
 *
 * Implementation note: we use `fetch` with `keepalive: true` + explicit
 * `credentials: "omit"` rather than `navigator.sendBeacon`. Beacon
 * unconditionally sends with credentials, which trips CORS preflight
 * against AlphaServer's wildcard `Access-Control-Allow-Origin: *` in dev
 * (cross-origin from `localhost:8002` to `localhost:3000`). `keepalive`
 * preserves the survives-page-unload behavior we wanted from beacon.
 *
 * Note: `keepalive: true` enforces a 64 KiB cap on the combined size of
 * all in-flight keepalive bodies in a tab. Today's server-side caps
 * (256-char message + small flat metadata) are well under that. If those
 * caps are ever raised, double-check we still fit.
 *
 * @param event   - tag identifying the event class. Must be in
 *                  `BrowserEvent`; the server rejects unknown values.
 * @param message - short human-readable description (server caps to
 *                  256 chars).
 * @param metadata - optional flat record of primitives (string / number /
 *                  boolean). Nested objects are dropped server-side.
 */
export function logFromBrowser(
  event: BrowserEvent,
  message: string,
  metadata?: Record<string, string | number | boolean>
): void {
  // SSR / non-browser environments — no-op.
  if (typeof fetch === "undefined") {
    return;
  }
  // No-op if `initBrowserLog` hasn't been called yet. Logging must never
  // break the calling flow, and dropping a few entries during startup
  // before the URL is wired up is preferable to throwing.
  if (!alphaServerUrl) {
    return;
  }
  try {
    void fetch(`${alphaServerUrl}${BROWSER_EVENT_PATH}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ event, message, metadata }),
      credentials: "omit",
      keepalive: true,
    }).catch(() => {
      // Swallow — observability must never break the calling flow.
    });
  } catch {
    // Defensive: synchronous throws (e.g. malformed URL) also swallowed.
  }
}
